// TypeScript shapes for the question `content` JSON we render. Mirrors the
// backend Pydantic models in backend/app/parts/_question_schemas.py (snake_case).
// Only the types with a first-class web renderer are modelled; everything else
// flows through the unknown fallback.

export interface MCQOption {
  id: string;
  text: string;
}

export interface MultipleChoiceContent {
  question: string;
  options: MCQOption[];
  correct_option_id: string;
  correct_option_ids: string[];
  explanation: string;
}

/** Shared shape for the many single-select MCQ-family types (error_correction,
 *  scenario_application, analogy, concept_map, cause_effect, debate, mnemonic,
 *  image_question). They all carry options + correct_option_id + explanation
 *  plus type-specific prose fields used to build the prompt. */
export interface McqFamilyContent {
  options: MCQOption[];
  correct_option_id: string;
  explanation: string;
  [key: string]: unknown;
}

export interface TrueFalseContent {
  statement: string;
  correct: boolean;
  explanation: string;
}

export interface FillBlankSlot {
  index: number;
  correct: string;
  distractors: string[];
  accepted_alternatives: string[];
  competence: string;
}

export interface FillBlankContent {
  template: string;
  blank_count: number;
  blanks: FillBlankSlot[];
  explanation: string;
}

export interface ClozePassageContent {
  passage: string;
  blank_count: number;
  blanks: FillBlankSlot[];
  topic_sentence: string;
  explanation: string;
}

export interface FlashcardContent {
  front: string;
  back: string;
  tags: string[];
}

export interface ShortAnswerContent {
  question: string;
  model_answer: string;
  rubric_keywords: string[];
  explanation: string;
}

export interface OrderingStep {
  token_id: string;
  index: number;
  text: string;
  is_fixed: boolean;
}

export interface OrderingContent {
  prompt: string;
  steps: OrderingStep[];
  explanation: string;
}

export interface DefinitionPair {
  term_id: string;
  term: string;
  definition: string;
}

export interface DefinitionMatchContent {
  instruction: string;
  pairs: DefinitionPair[];
  explanation: string;
}

export interface VocabPair {
  pair_id: string;
  de: string;
  en: string;
  hint: string;
}
export interface VocabPairContent {
  prompt: string;
  pairs: VocabPair[];
  explanation: string;
}

export interface InflectionCell {
  cell_id: string;
  label: string;
  answer: string;
  hint: string;
}
export interface InflectionTableContent {
  prompt: string;
  table_title: string;
  cells: InflectionCell[];
  explanation: string;
}

export interface ReadingOption {
  id: string;
  text: string;
  is_correct: boolean;
  rationale: string;
}
export interface ReadingSubQuestion {
  id: string;
  prompt: string;
  options: ReadingOption[];
}
export interface ReadingComprehensionContent {
  prompt: string;
  passage: string;
  sub_questions: ReadingSubQuestion[];
  explanation: string;
}

export interface ErrorToken {
  id: string;
  text: string;
  is_error: boolean;
}
export interface ErrorIdentificationContent {
  prompt: string;
  sentence_tokens: ErrorToken[];
  correction: string;
  explanation: string;
}

/** equation_builder — assemble a LaTeX equation by placing tiles from a shared
 *  pool into `___` blanks in the template. Tiles are reusable; all-or-nothing
 *  grading against `correct` (length = blank count). */
export interface EquationBuilderContent {
  prompt: string;
  equation_template: string;
  pool: string[];
  correct: string[];
  explanation: string;
}

/** derivation — pick the correct subset of steps from a shuffled pool (correct
 *  steps + distractors) and arrange them in derivation order. */
export interface DerivationContent {
  prompt: string;
  target_statement: string;
  steps: OrderingStep[];
  distractors: string[];
  explanation: string;
}

/** image_labeling — "relabel the diagram". The image has numbered markers (1..N)
 *  where part-labels were stripped; the learner assigns a label from `label_pool`
 *  to each marker. `markers[].correct` is the answer key; per-marker grading. */
export interface LabelMarker {
  id: number;
  correct: string;
}

export interface ImageLabelingContent {
  image_url: string;
  image_key?: string;
  prompt: string;
  markers: LabelMarker[];
  label_pool: string[];
  caption?: string;
  explanation: string;
}

/** crossword — a vertical theme word with horizontal entries each crossing it at
 *  exactly one shared letter. `answer` is backend-folded (uppercase, Ä→AE etc.).
 *  All-or-nothing: every entry's normalized answer must match. */
export interface CrosswordEntry {
  id: string;
  row_offset: number;
  cross_letter_index: number;
  answer: string;
  clue: string;
}
export interface CrosswordContent {
  prompt: string;
  theme_word: string;
  entries: CrosswordEntry[];
  explanation: string;
}

// ── Language-trainer / de-trainer drill types (mirror iOS QuestionModels.swift) ──

export interface VocabPoolPair {
  pair_id: string;
  de: string;
  en: string;
  hint: string;
  domain_tag: string;
}
export interface VocabPairPoolContent {
  prompt: string;
  pairs: VocabPoolPair[];
  explanation: string;
}

export interface ArticleNoun {
  id: string;
  noun: string;
  noun_gender: string;
  correct_article: string;
  hint: string;
}
export interface ArticleDrillBatchContent {
  prompt: string;
  case_label: string;
  articles: string[];
  items: ArticleNoun[];
  explanation?: string;
}

export interface RedemittelItem {
  id: string;
  sentence: string;
  trigger_text: string;
  function_label: string;
  near_miss_label: string;
  near_miss_rationale: string;
  explanation: string;
}
export interface RedemittelLabelContent {
  prompt: string;
  tray_labels: string[];
  items: RedemittelItem[];
  explanation: string;
}

export interface ConnectorItem {
  id: string;
  sentence: string;
  connector: string;
  correct_class: string;
  near_miss_class: string;
  near_miss_rationale: string;
  explanation: string;
}
export interface ConnectorClassifyContent {
  prompt: string;
  tray_classes: string[];
  items: ConnectorItem[];
  explanation: string;
}

export interface TransformationVariant {
  text: string;
  is_correct: boolean;
  rationale: string;
}
export interface TransformationPairContent {
  transform_kind: string;
  render_mode: string; // "mcq" | "typed"
  prompt: string;
  source: string;
  target_primary: string;
  target_accepted: string[];
  variants: TransformationVariant[];
  explanation: string;
}

export interface TypedBlank {
  id: string;
  accepted_primary: string;
  accepted_alts: string[];
  casing: string; // "ignore" | "strict"
  umlaut_tolerance: boolean;
  max_typo_distance: number;
  hint: string;
}
export interface TypedRecallFillContent {
  prompt: string;
  context_passage: string;
  blanks: TypedBlank[];
  explanation: string;
}

export interface ClauseToken {
  token_id: string;
  text: string;
  is_fixed: boolean;
  anchor: string;
}
export interface SentenceClause {
  role: string;
  subordinator: string;
  role_label_de: string;
  tokens: ClauseToken[];
  correct_order: string[];
}
export interface SentenceReorderClausesContent {
  prompt: string;
  original_sentence: string;
  clauses: SentenceClause[];
  explanation: string;
}

export interface HauptPassage {
  id: string;
  title: string;
  text: string;
}
export interface HauptCandidate {
  id: string;
  text: string;
  is_main: boolean;
  rationale: string;
}
export interface HauptaussagenContent {
  prompt: string;
  passage: HauptPassage;
  candidates: HauptCandidate[];
  explanation: string;
}

export interface GapCandidate {
  label: string;
  sentence: string;
}
export interface GapSlot {
  index: number;
  candidates: GapCandidate[];
  correct_label: string;
  distractor_reason: string;
  context_before: string;
  context_after: string;
}
export interface ParagraphGapSelectContent {
  prompt: string;
  passage: string;
  gaps: GapSlot[];
  explanation: string;
}

export interface LesenStatement {
  id: string;
  text: string;
  verdict: string; // "discussed" | "absent" | "contradicts"
  evidence_quote: string;
  rationale: string;
}
export interface LesenStatementCompareContent {
  prompt: string;
  passage: HauptPassage;
  statements: LesenStatement[];
  explanation: string;
}

export interface GuidedGapCandidate {
  label: string;
  text: string;
}
export interface GuidedGap {
  index: number;
  kind: string;
  candidates: GuidedGapCandidate[];
  correct_label: string;
}
export interface GuidedStatement {
  id: string;
  text: string;
  verdict: string;
  rationale: string;
}
export interface LesenGuidedPassageContent {
  prompt: string;
  passage: { text: string; gaps: GuidedGap[] };
  comprehension: { statements: GuidedStatement[] };
  explanation: string;
}

/** Types rendered by the shared single-select MCQ renderer. */
export const MCQ_FAMILY_TYPES = new Set<string>([
  "error_correction",
  "scenario_application",
  // `scenario` (item_kind="scenario") is a 4-option MCQ with a long situational
  // stem + rationale — fully interactive on iOS (ScenarioView). Routing it
  // through the MCQ renderer makes it interactive on web too (was falling back
  // to the "review and self-mark" unknown renderer).
  "scenario",
  "analogy",
  "concept_map",
  "cause_effect",
  "debate",
  "mnemonic",
  "image_question",
]);

/** Build the prompt text for an MCQ-family type from its specific fields. */
export function mcqFamilyPrompt(type: string, c: Record<string, unknown>): string {
  const s = (k: string) => (typeof c[k] === "string" ? (c[k] as string) : "");
  switch (type) {
    case "scenario_application":
      return [s("scenario"), s("question")].filter(Boolean).join("\n\n");
    case "scenario":
      return s("stem") || s("scenario") || s("prompt");
    case "error_correction":
      return [s("flawed_statement"), "Which correction is right?"].filter(Boolean).join("\n\n");
    case "analogy":
      return `${s("stem_a")} : ${s("stem_b")} :: ${s("stem_c")} : ?`;
    case "concept_map":
      return s("relationship_prompt") || `How do ${s("concept_a")} and ${s("concept_b")} relate?`;
    case "cause_effect":
      return [s("cause_statement"), s("effect_statement")].filter(Boolean).join("\n\n");
    case "debate":
      return [s("proposition"), s("question")].filter(Boolean).join("\n\n");
    case "mnemonic":
      return [s("mnemonic"), s("prompt")].filter(Boolean).join("\n\n");
    case "image_question":
      return s("prompt");
    default:
      return s("prompt") || s("question");
  }
}
